import React, { useEffect, useState } from 'react';
import { apis, hooks, models } from "@vote4dance/judging-sdk";
import { Typography, Tabs, notification, Space, Alert, Button } from 'antd';
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSchedulePages } from "../../../../base-react/hooks/Schedule";
import { SchedulePage } from "./components/SchedulePage";
import { HeaderContainer, BodyContainer } from "../../../../base-react/components/Container";
import { _T } from "../../../../base-react/src/Localization";
import { EditOutlined, EllipsisOutlined, EyeInvisibleOutlined, PrinterOutlined } from '@ant-design/icons';
import { ScheduleItemEdit } from './components/ScheduleItemEdit';
var Stream = hooks.streams.Stream;
var ScheduleSource = models.schedules.ScheduleSource;
import dayjs from 'dayjs';
import { Loading } from '../../../../base-react/components/Loading';
import { useCompetition } from '../../../../base-react/hooks/Competition';
var CompetitionsSource = models.competitions.CompetitionsSource;
var Api = apis.Api;
const { Title } = Typography;
const dateFormat = 'YYYY-MM-DD';
export const Schedule = () => {
    var _a;
    const { event_id, page_id, competition_id, class_id, round_id } = useParams();
    const [, event, , , , canEdit] = useCompetition(event_id);
    const [schedule, loading, error] = Stream(ScheduleSource(event_id));
    const { pages, pageData, defaultPageId } = useSchedulePages(event_id, schedule);
    const navigate = useNavigate();
    const location = useLocation();
    const [currentItem, setCurrentItem] = useState();
    const [editedItem, setEditedItem] = useState();
    const [visibleEdit, setVisibleEdit] = useState(false);
    const [publishing, setPublishing] = useState(false);
    useEffect(() => {
        error && notification.error({
            message: _T(error.name)
        });
    }, [error]);
    useEffect(() => {
        const locationParts = location.pathname.split('/');
        if (locationParts[locationParts.length - 1] === 'schedule') {
            if (pages.length > 0) {
                const todayPage = pages.find((page) => dayjs(page.schedule_date, dateFormat).isSame(dayjs(), 'day'));
                const targetPageId = todayPage ? todayPage.schedule_id : pages[0].schedule_id;
                navigate(`${targetPageId}${location.search}`, { replace: true });
            }
        }
    }, [pages, location]);
    useEffect(() => {
        setCurrentItem(pages.find(page => page.schedule_id == page_id));
    }, [pages, page_id]);
    const onChange = (activeKey) => {
        setCurrentItem(pages.find(page => page.schedule_id == activeKey));
    };
    const editPage = (id) => {
        setEditedItem(pages.find(page => page.schedule_id == id));
        setVisibleEdit(true);
    };
    const getPageChildren = (item) => {
        if (!item || !pages) {
            return [];
        }
        const pageIndex = pages.findIndex(page => page.schedule_id == item.schedule_id);
        return pageData[pageIndex] ? pageData[pageIndex] : [];
    };
    const createPage = () => {
        setEditedItem(undefined);
        setVisibleEdit(true);
    };
    const onEdit = (targetKey, action) => {
        if (action === 'add') {
            createPage();
        }
        else {
            editPage(targetKey);
        }
    };
    const scheduleVisibility = (_a = event === null || event === void 0 ? void 0 : event.competition_schedule_visibility) !== null && _a !== void 0 ? _a : 0;
    const hasPages = pages.length > 0;
    if (loading || (hasPages && !defaultPageId)) {
        return (React.createElement(React.Fragment, null,
            React.createElement(HeaderContainer, null,
                React.createElement(Title, null, _T('schedule.title'))),
            React.createElement(BodyContainer, null,
                React.createElement(Loading, null)),
            React.createElement(Outlet, null)));
    }
    const onPublishSchedule = async () => {
        if (!event) {
            return;
        }
        setPublishing(true);
        try {
            await Api.update(`${CompetitionsSource.url}/${event.competition_id}`, {
                ...event,
                competition_schedule_visibility: 1
            });
        }
        catch (err) {
            notification.error({
                message: err.message
            });
        }
        setPublishing(false);
    };
    const onUnpublishSchedule = async () => {
        if (!event) {
            return;
        }
        setPublishing(true);
        try {
            await Api.update(`${CompetitionsSource.url}/${event.competition_id}`, {
                ...event,
                competition_schedule_visibility: 0
            });
        }
        catch (err) {
            notification.error({
                message: err.message
            });
        }
        setPublishing(false);
    };
    const items = pages.map((pageItem, pageIndex) => ({
        key: pageItem.schedule_id,
        closeIcon: React.createElement(EditOutlined, null),
        closable: canEdit && pageItem.schedule_id && !['initial', 'unassigned'].includes(pageItem.schedule_id),
        label: React.createElement(Link, { to: '../schedule/' + pageItem.schedule_id, style: { paddingTop: 20, paddingBottom: 20 } }, pageItem.schedule_value),
        children: React.createElement(Space, { orientation: 'vertical', style: { width: '100%' } },
            pageItem.schedule_date && React.createElement(Title, { level: 2 }, dayjs(pageItem.schedule_date, dateFormat).format('LL')),
            React.createElement(SchedulePage, { event_id: event_id, schedule: schedule, pageItem: pageItem, pageData: pageData[pageIndex], pages: pages, disabled: !canEdit }))
    }));
    return (React.createElement(React.Fragment, null,
        React.createElement(HeaderContainer, null,
            React.createElement(Typography, null,
                React.createElement(Title, null, _T('schedule.title')))),
        React.createElement(BodyContainer, null,
            scheduleVisibility === 1 ? (React.createElement(Alert, { type: "success", title: _T('competition.details.competition_schedule_visibility.status.published'), action: React.createElement(Space, null,
                    React.createElement(Button, { size: "small", icon: React.createElement(PrinterOutlined, null), href: `/manager-react/print/${event_id}/schedule/` }),
                    React.createElement(Button, { size: "small", icon: React.createElement(EyeInvisibleOutlined, null), onClick: onUnpublishSchedule, loading: publishing, disabled: !canEdit })), showIcon: true, style: { marginBottom: 12, padding: '4px 8px' } })) : (React.createElement(Alert, { type: "info", title: _T('competition.details.competition_schedule_visibility.label'), description: _T('competition.details.competition_schedule_visibility.status.hidden'), action: React.createElement(Button, { type: "primary", onClick: onPublishSchedule, loading: publishing, disabled: !canEdit }, _T('competition.details.competition_schedule_visibility.action.publish')), showIcon: true, style: { marginBottom: 16 } })),
            React.createElement(Tabs, { defaultActiveKey: defaultPageId, activeKey: currentItem === null || currentItem === void 0 ? void 0 : currentItem.schedule_id, type: canEdit ? "editable-card" : "card", onChange: onChange, onEdit: onEdit, items: items, moreIcon: React.createElement(EllipsisOutlined, { style: { color: 'white' } }) })),
        React.createElement(ScheduleItemEdit, { event_id: event_id, isPage: true, pageChildren: getPageChildren(editedItem), schedule: schedule, scheduleItem: editedItem, visible: visibleEdit, setVisible: setVisibleEdit, disabled: !canEdit }),
        React.createElement(Outlet, null)));
};
